import { createContext, useContext, useEffect, useState } from "react";
import { useAuth, useUser } from "@clerk/clerk-react";
import { config } from "@/config/env";

type Role = "user" | "admin" | "super_admin";

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  role: Role | null;
  token: string | null;
  getToken: () => Promise<string | null>;
}

const AuthContext = createContext<AuthContextType>({
  isAuthenticated: false,
  isLoading: true,
  role: null,
  token: null,
  getToken: async () => null,
});

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const { isLoaded, isSignedIn, getToken } = useAuth();
  const { user } = useUser();
  const [role, setRole] = useState<Role | null>(null);
  const [isRoleLoading, setIsRoleLoading] = useState(true);
  const [token, setToken] = useState<string | null>(null);

  useEffect(() => {
    async function fetchRole() {
      if (!isSignedIn || !isLoaded) {
        setRole(null);
        setToken(null);
        setIsRoleLoading(false);
        return;
      }

      try {
        const authToken = await getToken();
        setToken(authToken);

        // If no API URL is configured, we can't fetch the role.
        // This prevents hitting the frontend server (localhost:3000) and getting HTML back.
        if (!config.apiUrl) {
          console.warn("BUN_PUBLIC_API_URL is not set. Skipping role fetch.");
          setRole(null); // Or default to 'user' if appropriate, but safer to be null/restricted
          setIsRoleLoading(false);
          return;
        }

        const url = `${config.apiUrl}/admin/access/me`;
        
        const response = await fetch(url, {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          setRole(data.role as Role);
        } else {
          console.error("Failed to fetch role:", response.status, response.statusText);
          setRole(null);
        }
      } catch (error) {
        console.error("Error fetching role:", error);
        setRole(null);
      } finally {
        setIsRoleLoading(false);
      }
    }

    fetchRole();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, isSignedIn]); // Removed getToken to prevent loops if it's unstable

  const value = {
    isAuthenticated: !!isSignedIn,
    isLoading: !isLoaded || isRoleLoading,
    role,
    token,
    getToken: async () => await getToken(),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAppAuth = () => useContext(AuthContext);
